'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus, X, Loader2, AlertTriangle, Home, User } from 'lucide-react';
import {
  householdsAPI,
  HouseholdMember,
  SurvivorNeed,
  SurvivorNeedCreate,
  NeedType,
  NeedStatus,
  NeedPriority,
  NEED_TYPE_LABELS,
  NEED_STATUS_LABELS,
} from '@/services/api/households';

const PRIORITY_COLORS: Record<NeedPriority, string> = {
  LOW: 'bg-gray-100 text-gray-700',
  MEDIUM: 'bg-blue-100 text-blue-700',
  HIGH: 'bg-orange-100 text-orange-700',
  URGENT: 'bg-red-100 text-red-700',
};

const STATUS_COLORS: Record<NeedStatus, string> = {
  IDENTIFIED: 'bg-yellow-100 text-yellow-800',
  IN_PROGRESS: 'bg-blue-100 text-blue-800',
  REFERRED: 'bg-purple-100 text-purple-800',
  RESOLVED: 'bg-green-100 text-green-800',
};

interface Props {
  householdId: number;
  members: HouseholdMember[];
}

export default function HouseholdNeedsTab({ householdId, members }: Props) {
  const [needs, setNeeds] = useState<SurvivorNeed[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [filterMember, setFilterMember] = useState<number | ''>('');
  const [filterStatus, setFilterStatus] = useState<NeedStatus | ''>('');
  const [filterScope, setFilterScope] = useState<'individual' | 'household' | ''>('');
  const [saving, setSaving] = useState(false);
  const [scope, setScope] = useState<'individual' | 'household'>('individual');
  const [form, setForm] = useState<SurvivorNeedCreate>({
    household_member_id: null,
    need_type: 'OTHER' as NeedType,
    priority: 'MEDIUM',
  });

  const loadNeeds = useCallback(async () => {
    setLoading(true);
    try {
      const data = await householdsAPI.listNeeds(householdId, {
        household_member_id: filterMember || undefined,
        need_status: filterStatus || undefined,
        scope: filterScope || undefined,
      });
      setNeeds(data);
    } catch (err) {
      console.error('Failed to load needs:', err);
    } finally {
      setLoading(false);
    }
  }, [householdId, filterMember, filterStatus, filterScope]);

  useEffect(() => {
    loadNeeds();
  }, [loadNeeds]);

  const handleCreate = async () => {
    if (scope === 'individual' && !form.household_member_id) return;
    setSaving(true);
    try {
      const payload: SurvivorNeedCreate = {
        ...form,
        household_member_id: scope === 'household' ? null : form.household_member_id,
      };
      await householdsAPI.createNeed(householdId, payload);
      setShowAdd(false);
      setScope('individual');
      setForm({ household_member_id: null, need_type: 'OTHER' as NeedType, priority: 'MEDIUM' });
      loadNeeds();
    } catch (err) {
      console.error('Failed to create need:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (needId: number, newStatus: NeedStatus) => {
    try {
      await householdsAPI.updateNeed(householdId, needId, { need_status: newStatus });
      loadNeeds();
    } catch (err) {
      console.error('Failed to update need status:', err);
    }
  };

  const handleDelete = async (needId: number) => {
    if (!confirm('Delete this need?')) return;
    try {
      await householdsAPI.deleteNeed(householdId, needId);
      loadNeeds();
    } catch (err) {
      console.error('Failed to delete need:', err);
    }
  };

  const activeMembers = members.filter((m) => m.status === 'ACTIVE');

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Survivor Needs</h3>
        <Button size="sm" onClick={() => setShowAdd(!showAdd)} className="gap-1.5">
          <Plus className="w-4 h-4" />
          Add Need
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-3 flex-wrap">
        <select
          value={filterMember}
          onChange={(e) => setFilterMember(e.target.value ? Number(e.target.value) : '')}
          className="border rounded-md px-3 py-1.5 text-sm"
        >
          <option value="">All Members</option>
          {activeMembers.map((m) => (
            <option key={m.id} value={m.id}>
              {m.first_name} {m.last_name}
            </option>
          ))}
        </select>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value as NeedStatus | '')}
          className="border rounded-md px-3 py-1.5 text-sm"
        >
          <option value="">All Statuses</option>
          {Object.entries(NEED_STATUS_LABELS).map(([val, label]) => (
            <option key={val} value={val}>
              {label}
            </option>
          ))}
        </select>
        <select
          value={filterScope}
          onChange={(e) => setFilterScope(e.target.value as 'individual' | 'household' | '')}
          className="border rounded-md px-3 py-1.5 text-sm"
        >
          <option value="">Individual + Household</option>
          <option value="individual">Individual only</option>
          <option value="household">Household-level only</option>
        </select>
      </div>

      {/* Add Need Form */}
      {showAdd && (
        <Card className="border-indigo-200 bg-indigo-50/30">
          <CardContent className="p-4 space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium">
                Add {scope === 'household' ? 'Household-level' : 'Individual'} Need
              </h4>
              <button onClick={() => setShowAdd(false)}>
                <X className="w-4 h-4 text-gray-400" />
              </button>
            </div>

            <div>
              <Label>Scope</Label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                <button
                  type="button"
                  onClick={() => setScope('individual')}
                  className={`flex items-center gap-2 px-3 py-2 rounded-md border text-sm ${
                    scope === 'individual'
                      ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                      : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <User className="w-4 h-4" />
                  Individual survivor
                </button>
                <button
                  type="button"
                  onClick={() => setScope('household')}
                  className={`flex items-center gap-2 px-3 py-2 rounded-md border text-sm ${
                    scope === 'household'
                      ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                      : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <Home className="w-4 h-4" />
                  Whole household
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Survivor</Label>
                {scope === 'individual' ? (
                  <select
                    value={form.household_member_id ?? 0}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, household_member_id: Number(e.target.value) || null }))
                    }
                    className="w-full border rounded-md px-3 py-2 text-sm"
                  >
                    <option value={0}>Select member...</option>
                    {activeMembers.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.first_name} {m.last_name}
                      </option>
                    ))}
                  </select>
                ) : (
                  <div className="w-full border rounded-md px-3 py-2 text-sm bg-gray-50 text-gray-500 italic">
                    Applies to the entire household
                  </div>
                )}
              </div>
              <div>
                <Label>Need Type</Label>
                <select
                  value={form.need_type}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, need_type: e.target.value as NeedType }))
                  }
                  className="w-full border rounded-md px-3 py-2 text-sm"
                >
                  {Object.entries(NEED_TYPE_LABELS).map(([val, label]) => (
                    <option key={val} value={val}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {form.need_type === 'OTHER' && (
              <div>
                <Label>Specify Other Need</Label>
                <Input
                  value={form.need_type_other || ''}
                  onChange={(e) => setForm((prev) => ({ ...prev, need_type_other: e.target.value }))}
                  placeholder="Describe the need..."
                />
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Priority</Label>
                <select
                  value={form.priority || 'MEDIUM'}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, priority: e.target.value as NeedPriority }))
                  }
                  className="w-full border rounded-md px-3 py-2 text-sm"
                >
                  <option value="LOW">Low</option>
                  <option value="MEDIUM">Medium</option>
                  <option value="HIGH">High</option>
                  <option value="URGENT">Urgent</option>
                </select>
              </div>
              <div>
                <Label>Initial Status</Label>
                <select
                  value={form.need_status || 'IDENTIFIED'}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, need_status: e.target.value as NeedStatus }))
                  }
                  className="w-full border rounded-md px-3 py-2 text-sm"
                >
                  {Object.entries(NEED_STATUS_LABELS).map(([val, label]) => (
                    <option key={val} value={val}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <Label>Notes</Label>
              <textarea
                value={form.need_notes || ''}
                onChange={(e) => setForm((prev) => ({ ...prev, need_notes: e.target.value }))}
                className="w-full border rounded-md px-3 py-2 text-sm min-h-[80px]"
                placeholder="Describe the specific need, context, and any resources identified..."
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setShowAdd(false)}>
                Cancel
              </Button>
              <Button
                size="sm"
                disabled={(scope === 'individual' && !form.household_member_id) || saving}
                onClick={handleCreate}
              >
                {saving ? 'Creating...' : 'Add Need'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Needs List */}
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-5 h-5 animate-spin text-indigo-600" />
        </div>
      ) : needs.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center">
            <AlertTriangle className="w-10 h-10 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-500">No needs recorded</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {needs.map((need) => {
            const isHouseholdScope = need.scope === 'HOUSEHOLD' || need.household_member_id == null;
            const subjectLabel = isHouseholdScope
              ? 'Whole household'
              : need.survivor_name || `${need.member_first_name || ''} ${need.member_last_name || ''}`.trim() || 'Member';
            return (
            <Card key={need.id}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-sm flex items-center gap-1.5">
                        {isHouseholdScope ? <Home className="w-3.5 h-3.5 text-indigo-600" /> : <User className="w-3.5 h-3.5 text-gray-500" />}
                        {subjectLabel}
                      </span>
                      {isHouseholdScope && (
                        <Badge className="bg-indigo-100 text-indigo-700" variant="secondary">
                          Household-level
                        </Badge>
                      )}
                      <Badge className={STATUS_COLORS[need.need_status]} variant="secondary">
                        {NEED_STATUS_LABELS[need.need_status]}
                      </Badge>
                      <Badge className={PRIORITY_COLORS[need.priority]} variant="secondary">
                        {need.priority}
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-700">
                      {NEED_TYPE_LABELS[need.need_type] || need.need_type}
                      {need.need_type === 'OTHER' && need.need_type_other && (
                        <span className="text-gray-500"> - {need.need_type_other}</span>
                      )}
                    </p>
                    {need.need_notes && (
                      <p className="text-xs text-gray-500 mt-1">{need.need_notes}</p>
                    )}
                    {need.created_at && (
                      <p className="text-xs text-gray-400 mt-1">
                        Created {new Date(need.created_at).toLocaleDateString()}
                      </p>
                    )}
                  </div>

                  {/* Status transition + delete */}
                  <div className="flex items-center gap-1">
                    <select
                      value={need.need_status}
                      onChange={(e) => handleStatusChange(need.id, e.target.value as NeedStatus)}
                      className="border rounded px-2 py-1 text-xs"
                    >
                      {Object.entries(NEED_STATUS_LABELS).map(([val, label]) => (
                        <option key={val} value={val}>
                          {label}
                        </option>
                      ))}
                    </select>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600"
                      onClick={() => handleDelete(need.id)}
                    >
                      <X className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
